/**
 * Expiry progress for ranged stable fetches ("3 months", "6 months", "1 year").
 *
 * The backend returns every expiry in the requested range, progressively: the first answer
 * carries the expiries already loaded plus `pending_expiries`, and `partial` stays true until
 * that list is empty. Before 2026-10-18 nothing here read those fields, so the table kept the
 * first answer (often 2-6 expiries) and looked capped. The table fetch now records the
 * progress of its last answer here, and App.loadData polls until it completes.
 */

export interface StableFetchProgress {
    url: string;
    partial: boolean;
    loaded: number;
    total: number;
    pending: string[];
    fetchId: string | null;
    /** Per-symbol expiry ceiling (documented sanity limit, default 60). */
    ceiling: number | null;
    /** Expiries inside the range that the ceiling left out (0 when none). */
    beyondCeiling: number;
    message: string | null;
    at: number;
}

/** Poll cadence while a ranged fetch is still loading expiries. */
export const STABLE_PROGRESS_POLL_MS = 5_000;
/**
 * Give up only after this many consecutive polls with no new expiry (~3 min), never on a
 * fixed attempt count: a wide index range at the provider's request budget can take
 * several minutes, and "more history = longer wait".
 */
export const STABLE_PROGRESS_MAX_IDLE_POLLS = 36;

let last: StableFetchProgress | null = null;

const num = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

export function extractStableProgress(
    url: string,
    json: any,
): StableFetchProgress | null {
    if (!json || typeof json !== "object") return null;
    const hasCounts =
        json.expiries_total !== undefined || json.partial !== undefined;
    if (!hasCounts) return null;
    const total = num(json.expiries_total);
    const loaded = num(json.expiries_loaded ?? total);
    return {
        url,
        partial: json.partial === true,
        loaded,
        total,
        pending: Array.isArray(json.pending_expiries)
            ? json.pending_expiries.map(String)
            : [],
        fetchId: typeof json.fetch_id === "string" ? json.fetch_id : null,
        ceiling:
            json.expiry_ceiling !== undefined ? num(json.expiry_ceiling) : null,
        beyondCeiling: num(json.expiries_beyond_ceiling),
        message:
            typeof json.progress_message === "string"
                ? json.progress_message
                : typeof json.message === "string"
                  ? json.message
                  : null,
        at: Date.now(),
    };
}

export function setLastStableProgress(p: StableFetchProgress | null): void {
    last = p;
}

export function getLastStableProgress(): StableFetchProgress | null {
    return last;
}

/** "12 of 38 expiries loaded" / Arabic; plus the ceiling note when it applied. */
export function describeStableProgress(
    p: StableFetchProgress,
    language: string = "en",
): string {
    const ar = language === "ar";
    let s = p.partial
        ? ar
            ? `${p.loaded} من ${p.total} تواريخ استحقاق محمّلة`
            : `${p.loaded} of ${p.total} expiries loaded`
        : ar
          ? `${p.total} تواريخ استحقاق`
          : `${p.total} expiries`;
    if (p.beyondCeiling > 0) {
        s += ar
            ? ` · ${p.beyondCeiling} خارج الحد الأقصى (${p.ceiling ?? 60} لكل رمز)`
            : ` · ${p.beyondCeiling} beyond the ${p.ceiling ?? 60}-expiry limit per symbol`;
    }
    return s;
}

export function delayMs(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(new DOMException("Aborted", "AbortError"));
            return;
        }
        const t = setTimeout(() => {
            signal?.removeEventListener("abort", onAbort);
            resolve();
        }, ms);
        const onAbort = () => {
            clearTimeout(t);
            reject(new DOMException("Aborted", "AbortError"));
        };
        signal?.addEventListener("abort", onAbort, { once: true });
    });
}

/** Progress attached by dataService.fetchStableData to the rows it returned (or null). */
export function stableProgressOf(rows: unknown): StableFetchProgress | null {
    const p = (rows as any)?.__stableProgress;
    return p && typeof p === "object" ? (p as StableFetchProgress) : null;
}
